'use client'

import { X24 } from '@vinted/monochrome-icons'
import { Button, Cell, Dialog, InfoBanner, Navigation, Spacer, Text } from '@vinted/web-ui'
import { ReactNode } from 'react'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { clickEvent } from '@marketplace-web/linkified-message/linkified-message-data'
import { HelpCenterFaqEntryUrl } from '@marketplace-web/help-center/help-center-faq-entry-url-feature'
import {
  AccessChannel,
  FaqEntryType,
} from '@marketplace-web/help-center/help-center-faq-entry-url-data'

type Props = {
  url: string | undefined
  eventTargetDetails: string
  onClose(): void
}

const LeavingVintedModal = ({ url, eventTargetDetails, onClose }: Props) => {
  const translate = useTranslate('conversation.conversation_info.leaving_vinted_dialog')
  const translateA11y = useTranslate('common.a11y.actions')
  const { track } = useTracking()

  const handleClose = () => {
    track(
      clickEvent({
        target: 'cancel_leaving_modal',
        targetDetails: eventTargetDetails,
      }),
    )

    onClose()
  }

  const handleOnContinueClick = () => {
    track(
      clickEvent({
        target: 'proceed_from_leaving_modal',
        targetDetails: eventTargetDetails,
      }),
    )

    onClose()
  }

  const handleLearnMoreClick = () => {
    track(
      clickEvent({
        target: 'learn_more_leaving_modal',
        targetDetails: eventTargetDetails,
      }),
    )
  }

  const handleStayOnVintedClick = () => {
    track(
      clickEvent({
        target: 'stay_on_vinted_leaving_modal',
        targetDetails: eventTargetDetails,
      }),
    )

    onClose()
  }

  const learnMoreTranslationValues = {
    'link-learn-more': (chunks: Array<ReactNode>) => (
      <HelpCenterFaqEntryUrl
        key="link-learn-more"
        type={FaqEntryType.SpamPhishing}
        accessChannel={AccessChannel.ProductLink}
        render={faqUrl => (
          <a href={faqUrl} target="_blank" rel="noreferrer" onClick={handleLearnMoreClick}>
            {chunks}
          </a>
        )}
      />
    ),
  }

  return (
    <Dialog show={!!url} testId="leaving-vinted-modal">
      <Navigation
        right={
          <Button
            aria={{ 'aria-label': translateA11y('dialog_close') }}
            iconName={X24}
            styling={Button.Styling.Flat}
            onClick={handleClose}
            testId="leaving-vinted-modal-close-button"
            inline
          />
        }
      />
      <Cell styling={Cell.Styling.Wide}>
        <Text
          as="h1"
          type={Text.Type.Heading}
          alignment={Text.Alignment.Center}
          width={Text.Width.Parent}
          text={translate('title')}
          testId="leaving-vinted-modal--title"
        />
        <Spacer size={Spacer.Size.Large} />
        <Text as="span" text={translate('description', learnMoreTranslationValues)} />
        <Spacer size={Spacer.Size.Large} />
        <InfoBanner
          type={InfoBanner.Type.Warning}
          body={
            <>
              {translate('tap_to_continue')}{' '}
              <a
                href={url}
                onClick={handleOnContinueClick}
                className="u-text-wrap"
                target="_blank"
                rel="noopener noreferrer"
              >
                {url?.replace(/^\/\//, '')}
              </a>
            </>
          }
          styling={InfoBanner.Styling.Tight}
        />
        <Spacer size={Spacer.Size.X2Large} />
        <Button
          onClick={handleStayOnVintedClick}
          styling={Button.Styling.Filled}
          text={translate('stay_on_vinted')}
          testId="leaving-vinted-modal--cancel-button"
        />
      </Cell>
    </Dialog>
  )
}

export default LeavingVintedModal
